import { useEffect, useRef, useState } from 'react';
import { AppState } from 'react-native';

import { getLocalTime } from '../../common/utils/time/time';

// Long JS timers are unreliable on mobile, so the midnight check re-arms at least hourly.
const MAX_CHECK_DELAY_MS = 60 * 60 * 1000;

const formatToday = timezone => getLocalTime(timezone).format('YYYY-MM-DD');

/**
 * Today's date (YYYY-MM-DD) in the tenant timezone that rolls over to the new
 * day while the app stays open: checked at local midnight and whenever the app
 * returns to the foreground. Re-renders only when the date actually changes.
 */
const useTodayDateKey = (timezone) => {
	const [today, setToday] = useState(() => ({ timezone, dateKey: formatToday(timezone) }));
	const todayRef = useRef(today);
	todayRef.current = today;

	useEffect(() => {
		let timeout = null;
		const checkToday = () => {
			const dateKey = formatToday(timezone);
			if (todayRef.current.timezone !== timezone || todayRef.current.dateKey !== dateKey) {
				setToday({ timezone, dateKey });
			}
		};
		const scheduleMidnightCheck = () => {
			const now = getLocalTime(timezone);
			const untilMidnight = now.clone().add(1, 'day').startOf('day').diff(now) + 1000;
			timeout = setTimeout(() => {
				checkToday();
				scheduleMidnightCheck();
			}, Math.min(untilMidnight, MAX_CHECK_DELAY_MS));
		};

		checkToday();
		scheduleMidnightCheck();
		const subscription = AppState.addEventListener('change', (nextAppState) => {
			if (nextAppState === 'active') {
				checkToday();
			}
		});

		return () => {
			clearTimeout(timeout);
			subscription.remove();
		};
	}, [timezone]);

	// A timezone change applies in the same render, as the previous memoised date did.
	return today.timezone === timezone ? today.dateKey : formatToday(timezone);
};

export default useTodayDateKey;
